import React, { useState } from 'react';
import { Drawer, Form, message } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { useIntl } from 'react-intl';
import { Button, Input, Checkbox } from 'ui';
import CategorySelect from 'admin/Selects/CategorySelect';
import RichTextInput, { type RichTextInputHandle } from 'ui/Input/ReactQuillInput';
import useQueryApiClient from 'utils/useQueryApiClient';
import { CreateEditPagesGallery } from 'ui/UploadImage/Pages/CreateEditPagesGallery';

interface PageDrawerProps {
  open: boolean;
  onClose: () => void;
  pageId?: number;
  setReload: React.Dispatch<React.SetStateAction<number>>;
}

type UploadFileEx = UploadFile & {
  existing?: boolean;
  id?: number;
};

export const PageDrawer = ({ open, onClose, pageId, setReload }: PageDrawerProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [fileList, setFileList] = useState<UploadFileEx[]>([]);
  const isEditMode = !!pageId;
  const requestUrl = isEditMode ? `/api/v2/pages/${pageId}` : '/api/v2/pages';

  const textRef = React.useRef<RichTextInputHandle>(null);
  const seoRef  = React.useRef<RichTextInputHandle>(null);

  const { appendData: fetchImageBlob, isLoading: isImageLoading } = useQueryApiClient({
    request: { url: '/api/v2/image', multipart: true, disableOnMount: true },
    onSuccess: (blob: Blob, pass?: { imageUrl?: string; id?: number; name?: string }) => {
      if (!blob) return;

      const url = pass?.imageUrl || 'unknown_file';
      const dbId = pass?.id;
      const suggestedName =
        pass?.name ||
        (url.split('/').pop() || 'unknown_file').split('?')[0];

      const rcFile = new File([blob], suggestedName, { type: blob.type }) as RcFile;
      rcFile.uid = `${Date.now()}-${Math.random()}`;
      const objUrl = URL.createObjectURL(rcFile);

      const uploadFile: UploadFileEx = {
        uid: rcFile.uid,
        name: rcFile.name,
        originFileObj: rcFile,
        status: 'done',
        url: objUrl,
        preview: objUrl,
        existing: true,
        id: dbId,
      };

      setFileList(prev => [...prev, uploadFile]);
    },
  });

  const { appendData: refetchPage } = useQueryApiClient({
    request: { url: `/api/v2/pages/${pageId}`, method: 'GET', disableOnMount: true },
    onSuccess: (response) => {
      const normalized = {
        ...response,
        public: !!response?.public,
        system_msg: !!response?.system_msg,
      };
      form.setFieldsValue(normalized);

      if (Array.isArray(response?.images)) {
        setFileList([]);
        response.images.forEach((img: any) => {
          const imageUrl: string = img?.url_original || img?.url || '';
          if (imageUrl) {
            fetchImageBlob(
              { path: imageUrl },
              [],
              { imageUrl, id: img?.id, name: img?.name }
            );
          }
        });
      }
    },
  });

  React.useEffect(() => {
    if (open && isEditMode) {
      refetchPage();
    } else if (open && !isEditMode) {
      form.resetFields();
    }
  }, [open, isEditMode, pageId]);

  const { appendData: savePage, isLoading } = useQueryApiClient({
    request: { url: requestUrl, method: 'POST', multipart: true },
    onSuccess: () => {
      message.success(
        intl.formatMessage({
          id: isEditMode ? 'form.successfully_updated' : 'form.successfully_created',
        })
      );
      setReload((old) => old + 1);
      handleClose();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 422 && data?.errors && typeof data.errors === 'object') {
        const lines: string[] = Object.entries(data.errors).flatMap(
          ([field, msgs]) => (Array.isArray(msgs) ? msgs : [String(msgs)]).map(m => `${field}: ${m}`)
        );
        message.error({ content: <div>{lines.map((l, i) => <div key={i}>{l}</div>)}</div>, duration: 8 });
        return;
      }

      const msg =
        data?.message ||
        error?.message ||
        intl.formatMessage({ id: 'form.error' });

      message.error(msg);
    },
  });

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const formData = new FormData();

      if (isEditMode) formData.append('_method', 'PATCH');

      Object.entries(values).forEach(([key, val]) => {
        if (key === 'images') return;

        if (typeof val === 'boolean') {
          formData.append(key, val ? '1' : '0');
        } else if (val === null || val === undefined) {
          formData.append(key, '');
        } else {
          formData.append(key, String(val));
        }
      });

      const existingIds = fileList
        .filter(f => f.existing && typeof f.id === 'number' && !f.originFileObj)
        .map(f => String(f.id));
      formData.append('existing_ids', JSON.stringify(existingIds));

      fileList
        .filter(f => !f.existing || (f.existing && f.originFileObj))
        .forEach((f) => {
          const src = f.originFileObj as RcFile;
          if (!src) return;
          const realFile = new File([src], src.name, {
            type: src.type,
            lastModified: (src as any).lastModified || Date.now(),
          });
          formData.append('images[]', realFile, realFile.name);
        });

      savePage(formData);
    } catch (e: any) {
      if (e?.errorFields?.length) {
        const lines: string[] = e.errorFields.flatMap(
          (f: { name: (string | number)[]; errors: string[] }) =>
            (f.errors || []).map((msg) => `${f.name.join('.')}: ${msg}`)
        );
        form.scrollToField(e.errorFields[0].name);

        const first = e.errorFields[0]?.name?.[0];
        if (first === 'text') textRef.current?.focus();
        if (first === 'seo_text') seoRef.current?.focus();

        message.error({
          content:
            <div>
              {lines.map((l: string, i: number) => <div key={i}>{l}</div>)}
            </div>,
          duration: 6
        });
        return;
      }
      message.error(e?.message || 'Error');
    }
  };

  const handleSaveClick = () => {
    const fieldLabels: Record<string, string> = {
      title: intl.formatMessage({ id: 'pages.title_of_article' }),
      text: intl.formatMessage({ id: 'pages.text' }),
      seo_text: intl.formatMessage({ id: 'pages.seo_description' }),
    };

    const labelFromClientName = (name: (string | number)[]) => {
      const base = String(name?.[0] ?? '');
      return fieldLabels[base] || name.join('.');
    };

    form
      .validateFields()
      .then(() => {
        handleSubmit();
      })
      .catch((err) => {
        if (err?.errorFields?.length) {
          const lines: string[] = err.errorFields.flatMap(
            (f: { name: (string | number)[]; errors: string[] }) =>
              (f.errors || []).map((msg) => `${labelFromClientName(f.name)} ${msg}`)
          );

          form.scrollToField(err.errorFields[0].name);

          const first = err.errorFields[0]?.name?.[0];
          if (first === 'text') textRef.current?.focus();
          if (first === 'seo_text') seoRef.current?.focus();

          message.error({
            content: <div>{lines.map((l, i) => <div key={i}>{l}</div>)}</div>,
            duration: 6,
          });
          return;
        }
      });
  };

  return (
    <Drawer
      title={
        isEditMode
          ? intl.formatMessage({ id: 'general.edit_page_title' })
          : intl.formatMessage({ id: 'general.create_page_title' })
      }
      width={800}
      open={open}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button label={intl.formatMessage({ id: 'general.cancel' })} onClick={handleClose} />
          <Button
            type="primary"
            label={intl.formatMessage({ id: 'general.save' })}
            onClick={handleSaveClick}
            loading={isLoading || isImageLoading}
          />
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Input
          name="title"
          label={intl.formatMessage({ id: 'pages.title_of_article' })}
          validations={['required', 'maxLength255']}
        />

        <Input
          name="seo_url"
          label={intl.formatMessage({ id: 'pages.seo_site' })}
          disabled
        />

        <Input
          name="system_url"
          label={intl.formatMessage({ id: 'pages.systems_site' })}
          disabled
        />

        <Form.Item
          name="page_category"
          label={intl.formatMessage({ id: 'pages.article_category' })}
        >
          <CategorySelect name="page_category" />
        </Form.Item>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <Checkbox
            name="public"
            label={intl.formatMessage({ id: 'pages.published' })}
          />

          <Checkbox
            name="system_msg"
            label={intl.formatMessage({ id: 'pages.system_notification' })}
          />
        </div>

        <Form.Item
          label={intl.formatMessage({ id: 'general.gallery_title' })}
        >
          <CreateEditPagesGallery
            onFileListChange={setFileList}
            fileList={fileList}
          />
        </Form.Item>

        <RichTextInput
          ref={textRef}
          name="text"
          label={intl.formatMessage({ id: 'pages.text' })}
          maxBytes={8 * 1024 * 1024} // 8 MiB cap
        />

        <RichTextInput
          ref={seoRef}
          name="seo_text"
          label={intl.formatMessage({ id: 'pages.seo_description' })}
          rules={['maxLength']}
          max={255}
        />
      </Form>
    </Drawer>
  );
};
